import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import zIndex from '@mui/material/styles/zIndex';

// Assets
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as CheckBoxDefault } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckBoxFilled } from "assets/images/icons/checkbox_fiilled.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";
import { ReactComponent as UploadIcon } from "assets/images/icons/icn_btn_export_pressed.svg";

// Components
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import ImportView from "./ImportView";

const UploadView = ({
  viewStateEnum,
  getViewTitle,
  form,
  handleUploadView,
}) => {
  const theme = useTheme();
  const viewRef = useRef(null);

  // Status
  const [openImportOverlay, setOpenImportOverlay] = useState(false);
  const [importedKeys, setImportedKeys] = useState([]);
  const [replaceFlag, setReplaceFlag] = useState(true);

  // Status for mask calculation
  const [viewWidth, setViewWidth] = useState(0);
  const [viewHeight, setViewHeight] = useState(0);

  useEffect(() => {
    if (viewRef.current) {
      setViewWidth(viewRef.current.offsetWidth);
      setViewHeight(viewRef.current.offsetHeight);
    }
  }, [openImportOverlay]);

  // Method
  const downloadCSVExample = () => {
    const csvContent = "data:text/csv;charset=utf-8,License Key\nXXXXX-XXXXX-XXXXX-XXXXX\nABCDE-FGHIJ-KLMNO-PQRST";
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "license_key_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleImport = () => {
    handleUploadView.importKeys(replaceFlag, importedKeys);
  }

  const handleImportOverlay = {
    closeView: () => setOpenImportOverlay(false),
    updateKeys: (keys) => {
      setImportedKeys(keys);
      setOpenImportOverlay(false);
    }
  };

  let marginTop = 0;
  if (viewHeight > 0) {
    marginTop = viewHeight / 6;
  }

  return (
    <>
      <Box
        ref={viewRef}
        sx={{ p: 0, position: 'relative' }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              paddingLeft: "20px",
            }}
          >
            {getViewTitle(viewStateEnum.UPLOAD)}
          </Typography>
          <Button
            onClick={handleUploadView.closeView}
            sx={{
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box sx={{ paddingLeft: "26px", mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, mr: 2 }}>
            <OutlineRectangleButton
              startIcon={<AddIcon />}
              onClick={downloadCSVExample}
              height="32px"
            >
              Download CSV Sample
            </OutlineRectangleButton>
          </Box>

          <Box
            style={{
              border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
              borderRadius: '4px',
              margin: '0 16px 0 0',
            }}
          >
            <TableContainer
              component={Paper}
              sx={{
                "&.MuiPaper-root": {
                  boxShadow: "none",
                  border: 'none',
                },
              }}
              style={{ maxHeight: '300px', overflowY: 'auto' }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow style={{ borderBottom: `1px solid ${theme.palette.primary.borderGreyPrimary}` }}>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>License Key</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importedKeys.length === 0 ? (
                    <TableRow>
                      <TableCell align="center" sx={{ py: 8, color: theme.palette.primary.greyPrimary }}>
                        No file imported yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    importedKeys.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: 'rgb(246, 246, 246)' },
                          "&:nth-of-type(even)": { backgroundColor: theme.palette.primary.white },
                        }}
                      >
                        <TableCell sx={{ border: 'none' }}>{item.keySegments.join("-")}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ ...form.getBannerStyle(), mr: 2 }}>
            <Typography variant="body2">
              Credit amounts are revealed only after the server validates each key on the <strong>Apply</strong> step.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mr: 2 }}>
            <Typography sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
              License Keys: {importedKeys.length}
            </Typography>
            <OutlineRectangleButton
              startIcon={<UploadIcon />}
              onClick={() => setOpenImportOverlay(true)}
              height="32px"
            >
              Upload
            </OutlineRectangleButton>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Checkbox
              icon={<CheckBoxDefault style={{ width: "24px", height: "24px" }} />}
              checkedIcon={<CheckBoxFilled style={{ width: "24px", height: "24px" }} />}
              checked={replaceFlag}
              onChange={(e) => setReplaceFlag(e.target.checked)}
            />
            <Typography component="span" sx={{ fontSize: '14px' }}>
              Replace the existing keys in the list.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Note:</Typography>
            <Typography variant="body2" sx={{ color: theme.palette.primary.greyPrimary }}>1. Each license key on a separate line.</Typography>
          </Box>
        </Box>

        <Box
          sx={{
            margin: "24px 0 16px auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <OutlineButton
            onClick={handleUploadView.closeView}
            width="100px"
            height="32px"
            sx={{ mr: 2 }}
          >
            Cancel
          </OutlineButton>
          <SolidButton
            onClick={handleImport}
            width="100px"
            height="32px"
            disabled={importedKeys.length === 0}
          >
            Import
          </SolidButton>
        </Box>

        {openImportOverlay && (
          <Box
            style={{
              ...form.getMaskStyle(),
              width: viewWidth + 40 + 'px',
              height: viewHeight + 60 + 'px',
              transform: `translateY(-20px) translateX(-20px)`,
            }}
          />
        )}
      </Box>

      {openImportOverlay && (
        <Box
          style={{
            ...form.getInnerViewStyle(zIndex),
            width: viewWidth > 0 ? (viewWidth - 40 + 'px') : '100%',
            transform: `translateY(${marginTop}px) translateX(40px)`,
            marginTop: marginTop,
          }}
        >
          <Box style={form.getInnerContainerStyle()}>
            <ImportView
              viewStateEnum={viewStateEnum}
              getViewTitle={() => "Import License Key List"}
              form={form}
              handleImportOverlay={handleImportOverlay}
            />
          </Box>
        </Box>
      )}
    </>
  )
}

export default UploadView;
